import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  DrawerPresence,
  LoadingButton,
  LocalSearchField,
  PageHeader,
  type DataTableColumn,
} from '@zarya/ui';
import { isApiError } from '@app/api/ApiError';
import { errorView } from '@app/errors';
import { dataHour, hourSeconds } from '@app/format';
import { t, tn } from '@app/i18n';
import { useDrawerParam } from '@app/navigation';
import {
  NEWER_PAGE,
  PERIODOS,
  sinceNow,
  useDecisions,
  useIdentities,
  windowLabel,
  type DecisionPage,
  type FilterDecisions,
  type NewerDecisions,
  type PeriodID,
} from './Decisions.api';
import { CAUSAS, causaVocab, causaOf, type Causa } from './causa';
import { CHAT_WINDOW_PARAMS, chatWindowOfURL, type ChatWindow } from './chatWindow';
import { DecisionsFilters, type DecisionsFiltersValue } from './DecisionsFilters';
import { DecisionDrawer } from './DecisionDrawer';
import { DecisionTargetCell } from './DecisionTargetCell';
import { VerdictBadge } from './VerdictBadge';
import {
  duration,
  decisionIdentity,
  decisionMethod,
  decisionPerson,
  executorSourceLabel,
  sourceLabel,
  type Decision,
} from './vocabulario';
import s from './Decisions.module.css';

/**
 * T16 — Decisões. Lidas do histórico do control plane, que ingere as decisões do
 * PDP do cliente via middleware e as guarda pela retenção configurada (o «nada é
 * persistido» do FR-096 envelheceu com o histórico: sem fonte de log, o que para
 * é a ingestão — a lista continua com o que já foi guardado).
 *
 * Corte em relação ao protótipo (`rDecisions`): sem coluna **Sessão**.
 *
 * O chip **simulação** voltou (04/09), e a razão pela qual ele não existia
 * inverteu-se: o Simulador (T18) e a lente «Ver como» do mapa (T19) perguntam ao
 * **PDP de verdade** — é isso que faz a resposta valer —, e o PDP loga toda
 * avaliação no decision log. Ou seja, há evento simulado, e ele chegaria aqui
 * indistinguível do tráfego. A tela **corta** por padrão e o toggle «Incluir
 * simulações» os traz de volta, marcados. Nada disso é persistido pelo produto
 * (R11 continua de pé): quem guarda a decisão é o executor.
 */

/** Códigos que significam "a fonte não está de pé" (FR-098) — não são erro da tela. */
const UNAVAILABLE_SOURCE = new Set(['pdp_nao_configurado', 'rota_ausente']);

const INITIAL_FILTER: DecisionsFiltersValue = {
  periodo: '1h',
  veredito: 'todos',
  cause: null,
  identityId: null,
  tool: '',
  // Desligado: a tela fala de tráfego. Ligar por padrão poria ensaio no meio da
  // auditoria, que é o defeito que este filtro existe para não ter.
  includeSimulated: false,
  // Desligado pela mesma razão: a descoberta do executor não é tráfego (D1, aim-6ph.8).
  includeControlCalls: false,
};

/** Busca local (FR-096): pessoa, perfil ou `request_id`, só no que já carregou. */
function casaSearch(decisao: Decision, term: string): boolean {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  return [decisao.sub, decisao.profile, decisao.request_id]
    .filter(Boolean)
    .some((campo) => String(campo).toLowerCase().includes(q));
}

/**
 * A causa vinda do deep link `?cause=<x>` — o link que T17 e a documentação
 * escrevem (DEF-11: a tela ignorava o parâmetro e mostrava a lista inteira,
 * enquanto a API filtrava certo).
 *
 * VALOR FORA DO ENUM VIRA `null`, e não um filtro. `cause` é enum fechado do
 * contrato; mandá-lo cru à API devolveria 422 numa tela que só estava lendo um
 * link, e adotá-lo como filtro daria uma lista vazia que se lê como «não houve
 * decisão nenhuma» — as duas piores do que simplesmente não filtrar.
 */
function causeOfURL(raw: string | null): Causa | null {
  return raw !== null && (CAUSAS as string[]).includes(raw) ? (raw as Causa) : null;
}

/**
 * A fonte, para a linha de meta do cabeçalho.
 *
 * `store` e `executor` são vocabulário fechado e viram texto do catálogo. Quando
 * quem serviu foi o executor E ele soube dizer que leu do coletor, o detalhe
 * entra junto — é a informação que o DEF-04 pedia («de onde veio esta lista?»),
 * agora sem o nome de um produto de terceiro na tela.
 */
function sourceOf(page: DecisionPage): string {
  const served = sourceLabel(page.source);
  const executor = executorSourceLabel(page.executor_source);
  return executor ? `${served} · ${executor}` : served;
}

/** A frase da faixa «N novas»: «Mais de {n}» quando chegaram mais que a página da busca de 10 s. */
function newerText(newer: NewerDecisions): string {
  if (newer.many) return t('decisions.list.newer.many', { n: NEWER_PAGE });
  return tn('decisions.list.newer', newer.count);
}

/** Minutos do período, para resolver o `since` da janela. */
function minutesOf(period: PeriodID): number {
  return PERIODOS.find((p) => p.id === period)!.minutos ?? 60;
}

/** `since` de um filtro: relógio para os períodos fixos, o campo para o personalizado. */
function sinceDe(valor: DecisionsFiltersValue): string {
  if (valor.periodo === 'custom') {
    const data = valor.desde ? new Date(valor.desde) : null;
    if (data && !Number.isNaN(data.getTime())) return data.toISOString();
    return sinceNow(minutesOf('24h'));
  }
  return sinceNow(minutesOf(valor.periodo));
}

/**
 * Código do tique de ingestão que parou porque o executor não tem cursor de sequência —
 * `decisions.CodeSequenceCursorMissing` em `control-plane/backend/usecases/decisions/history.go` (aim-6ph.11).
 *
 * Copiado byte a byte de propósito: é valor de contrato, e o `ingestion_error` é `string` livre no schema (não há enum
 * gerado para cruzar). Se o backend o renomear, o banner cai no caso genérico e mostra o código cru — degrada para
 * verdadeiro-e-feio, nunca para falso.
 */
const SEQUENCE_CURSOR_MISSING = 'sequence_cursor_missing';

export function DecisionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // The Chat link (aim-5wc): read once, on mount, like `cause`. While it holds, the window is the conversation's.
  const [chatWindow, setChatWindow] = useState<ChatWindow | null>(() =>
    chatWindowOfURL(searchParams),
  );
  // Só na montagem: daqui em diante quem manda é o controle da tela, e reler o
  // parâmetro a cada render faria o filtro voltar sozinho ao valor do link.
  const [filters, setFiltros] = useState<DecisionsFiltersValue>(() => ({
    ...INITIAL_FILTER,
    cause: causeOfURL(searchParams.get('cause')),
  }));
  const [search, setBusca] = useState('');
  const drawer = useDrawerParam();

  const identidades = useIdentities();

  /**
   * O início da janela é **estado**, não derivado: `agora − 1 h` recalculado a
   * cada render trocaria a chave da query sem parar. Ele só se move quando o
   * período muda ou quando o usuário pede **Atualizar**.
   */
  const [since, setSince] = useState(
    () => chatWindow?.since ?? sinceNow(minutesOf(INITIAL_FILTER.periodo)),
  );
  /**
   * O topo da lista (aim-6ph.4): o `before` da primeira página, que é o instante em que a janela abriu. Ele anda junto
   * com o `since`: um período novo, ou «Atualizar», é outra janela contada a partir de agora.
   */
  const [before, setBefore] = useState(() => new Date().toISOString());
  const reopen = (nextSince: string) => {
    setSince(nextSince);
    setBefore(new Date().toISOString());
  };

  /** «Limpar»: the four params leave the URL, and the screen goes back to its own window. */
  const clearChatWindow = () => {
    const next = new URLSearchParams(searchParams);
    for (const name of CHAT_WINDOW_PARAMS) next.delete(name);
    setSearchParams(next, { replace: true });
    setChatWindow(null);
    reopen(sinceNow(minutesOf(filters.periodo)));
  };

  const swapFilters = (valor: DecisionsFiltersValue) => {
    // One `next` for the whole change: two `setSearchParams` from the same stale `searchParams` would let the second
    // put back what the first removed.
    const next = new URLSearchParams(searchParams);
    let urlChanged = false;
    if (valor.periodo !== filters.periodo || valor.desde !== filters.desde) {
      reopen(sinceDe(valor));
      // Another period is another window: the conversation's no longer holds (aim-5wc).
      if (chatWindow) {
        for (const name of CHAT_WINDOW_PARAMS) next.delete(name);
        setChatWindow(null);
        urlChanged = true;
      }
    }
    // A URL acompanha a causa nos dois sentidos: o link chega e seleciona, a
    // seleção volta para a URL e o link fica compartilhável. `replace` porque
    // trocar um filtro não é navegação — empilhar histórico faria o «voltar»
    // desfazer filtro em vez de sair da tela.
    if (valor.cause !== filters.cause) {
      if (valor.cause) next.set('cause', valor.cause);
      else next.delete('cause');
      urlChanged = true;
    }
    if (urlChanged) setSearchParams(next, { replace: true });
    setFiltros(valor);
  };

  const filter: FilterDecisions = useMemo(
    () => ({
      since,
      before,
      ...(filters.veredito !== 'todos' ? { veredito: filters.veredito } : {}),
      ...(filters.cause ? { cause: filters.cause } : {}),
      ...(filters.identityId ? { identityId: filters.identityId } : {}),
      ...(filters.tool.trim() ? { tool: filters.tool.trim() } : {}),
      ...(filters.includeSimulated ? { includeSimulated: true } : {}),
      ...(filters.includeControlCalls ? { includeControlCalls: true } : {}),
      ...(chatWindow ? { sub: chatWindow.sub, until: chatWindow.until } : {}),
    }),
    [
      since,
      before,
      chatWindow,
      filters.veredito,
      filters.cause,
      filters.identityId,
      filters.tool,
      filters.includeSimulated,
      filters.includeControlCalls,
    ],
  );

  // Cursor, dedup e ordem vivem no hook (T372); a tela só filtra localmente. A busca das novas para enquanto o
  // detalhe (T17) está aberto (§1.1 do protótipo do aim-6ph.4).
  const decisoes = useDecisions(filter, { paused: Boolean(drawer.id) });
  const carregadas = decisoes.itens;
  const visiveis = useMemo(
    () => carregadas.filter((decisao) => casaSearch(decisao, search)),
    [carregadas, search],
  );

  const first = decisoes.primeira;
  const erro = decisoes.error;
  const sourceOutside = erro && isApiError(erro) && UNAVAILABLE_SOURCE.has(erro.code);

  const columns: DataTableColumn<Decision>[] = [
    {
      key: 'ts',
      header: t('decisions.list.column.when'),
      mono: true,
      width: '132px',
      cell: (d) => <span className={s.muted}>{dataHour(d.ts) ?? d.ts}</span>,
    },
    {
      key: 'identidade',
      header: t('decisions.list.column.identity'),
      tip: t('decisions.list.column.identity.tip'),
      cell: (d) => {
        const identidade = decisionIdentity(d);
        return identidade.conhecida ? (
          <b>{identidade.texto}</b>
        ) : (
          <span className={s.spiffe} title={t('decisions.list.identityUngoverned')}>
            {identidade.texto}
          </span>
        );
      },
    },
    {
      key: 'pessoa',
      header: t('decisions.list.column.person'),
      width: '150px',
      tip: t('decisions.list.column.person.tip'),
      cell: (d) => {
        const pessoa = decisionPerson(d);
        return pessoa.anonima ? <span className={s.vazio}>{pessoa.texto}</span> : pessoa.texto;
      },
    },
    {
      key: 'tool',
      header: t('decisions.list.column.tool'),
      mono: true,
      tip: t('decisions.list.column.tool.tip'),
      cell: (d) => <DecisionTargetCell decision={d} />,
    },
    {
      key: 'rpc_method',
      header: t('decisions.list.column.method'),
      mono: true,
      width: '104px',
      cell: (d) => decisionMethod(d) ?? <span className={s.vazio}>—</span>,
    },
    {
      key: 'veredito',
      header: t('decisions.list.column.verdict'),
      width: '176px',
      cell: (d) => <VerdictBadge decision={d} />,
    },
    {
      key: 'cause',
      header: t('decisions.list.column.cause'),
      width: '150px',
      tip: t('decisions.list.column.cause.tip'),
      cell: (d) => {
        const vocab = causaVocab(causaOf(d.cause));
        // O `data-testid` separa o chip da causa do badge do veredito: com a
        // decisão 4 de 08/09 os dois dizem «ALLOW» numa decisão permitida.
        return (
          <span data-testid="causa">
            <Badge tone={vocab.tone} tip={vocab.tip}>
              {vocab.rotulo}
            </Badge>
          </span>
        );
      },
    },
    {
      key: 'rule',
      header: t('decisions.list.column.rule'),
      width: '132px',
      tip: t('decisions.list.column.rule.tip'),
      cell: (d) =>
        d.rule ? (
          <span className={s.regraCelula}>{d.rule}</span>
        ) : (
          // "sem regra", nao "—": nenhuma regra ter valido e a informacao que
          // explica "faltou no humano", e um traco a esconderia atras de "dado
          // faltando" — a mesma escolha que ja se faz em "sem pessoa".
          <span className={s.vazio}>{t('decisions.list.noRule')}</span>
        ),
    },
    {
      key: 'duracao',
      header: t('decisions.list.column.duration'),
      tip: t('decisions.list.column.duration.tip'),
      numeric: true,
      width: '88px',
      cell: (d) => duration(d.duration_ms),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('decisions.list.title')}
        icon="list"
        tip={t('decisions.list.purpose')}
        updatedLabel={
          first
            ? t('decisions.list.meta', {
                source: sourceOf(first),
                window: windowLabel(first.window_max_min),
              })
            : undefined
        }
        actions={
          <LoadingButton
            variant="out"
            icon="refresh"
            loading={decisoes.isFetching && !decisoes.isFetchingNextPage}
            loadingLabel={t('decisions.list.refresh.loading')}
            // The window of a Chat conversation is fixed: «Atualizar» reads it again instead of moving it to «now».
            onClick={() => (chatWindow ? decisoes.refetch() : reopen(sinceDe(filters)))}
          >
            {t('decisions.list.refresh')}
          </LoadingButton>
        }
      />

      {chatWindow ? (
        <div className={s.fromChat} data-testid="decisions-from-chat">
          <Alert tone="info" role="status">
            {t('decisions.filters.fromChat', {
              sub: chatWindow.sub,
              agent: chatWindow.agent,
              from: hourSeconds(chatWindow.since) ?? chatWindow.since,
              to: hourSeconds(chatWindow.until) ?? chatWindow.until,
            })}
          </Alert>
          <Button
            variant="out"
            size="sm"
            onClick={clearChatWindow}
            data-testid="decisions-from-chat-clear"
          >
            {t('decisions.filters.fromChatClear')}
          </Button>
        </div>
      ) : null}

      {/* FR-098: fonte de decisões fora não é falha da tela — é configuração. */}
      {sourceOutside ? (
        <Alert tone="danger">
          {/* `texto` já decide se o código entra entre parênteses: para código fora
              da tabela a própria frase o nomeia, e repeti-lo aqui poria o mesmo
              código duas vezes na linha. */}
          {t('decisions.list.unavailable', { reason: errorView(erro).texto })}{' '}
          <Link className={s.link} to="/infra">
            {t('decisions.list.openInfra')}
          </Link>
        </Alert>
      ) : null}

      {/* Dois banners de INGESTÃO, e a diferença é de FATO. `ingestionStopped` diz
          «a última leitura do executor falhou» — verdade quando a leitura falhou. Sem o
          cursor de sequência a leitura NÃO falhou: o executor respondeu, e não diz a
          ordem de chegada (T16 do adendo, §6.2 do protótipo).

          Por isso o ramo é por CÓDIGO e não por presença do campo, e o
          `ingestionStopped` fica para todos os outros: um código novo do backend
          cai no caso genérico com o código cru, que é feio mas verdadeiro — e
          visível o bastante para alguém vir cá pôr a frase dele. */}
      {first?.ingestion_error === SEQUENCE_CURSOR_MISSING ? (
        <div data-testid="decisions-sequence-cursor-missing">
          <Alert tone="warning">{t('decisions.list.sequenceCursorMissing')}</Alert>
        </div>
      ) : first?.ingestion_error ? (
        <div data-testid="decisions-ingestion-stopped" data-code={first.ingestion_error}>
          <Alert tone="warning">
            {t('decisions.list.ingestionStopped', { code: first.ingestion_error })}
          </Alert>
        </div>
      ) : null}

      {first?.since_adjusted ? (
        <Alert tone="warning">
          {t('decisions.list.sinceAdjusted', { when: dataHour(first.since) ?? '—' })}
        </Alert>
      ) : null}

      <Card
        title={t('decisions.list.card.title')}
        subtitle={
          search.trim()
            ? t('decisions.list.card.subtitleFiltered', {
                shown: visiveis.length,
                total: carregadas.length,
              })
            : t('decisions.list.card.subtitle', { n: carregadas.length })
        }
        bare
      >
        <DecisionsFilters
          valor={filters}
          onChange={swapFilters}
          identidades={(identidades.data ?? []).map((identidade) => ({
            value: identidade.id,
            label: identidade.slug ?? t('decisions.filters.identity.unassigned'),
            hint: identidade.spiffe_id_predicted ?? undefined,
          }))}
          windowMaxMin={first?.window_max_min}
        />

        {/* A faixa das novas (D4): aparece quando a busca de 10 s achou decisões mais novas que a primeira linha. A
            lista não muda sozinha; «Mostrar» põe as novas no topo. */}
        {decisoes.newer.count > 0 ? (
          <div
            className={s.newerBand}
            role="status"
            data-testid="decisions-newer"
            data-count={decisoes.newer.count}
            data-many={String(decisoes.newer.many)}
          >
            <span>
              <span aria-hidden="true">↑ </span>
              {newerText(decisoes.newer)}
            </span>
            <Button variant="out" size="sm" onClick={decisoes.newer.show} data-testid="decisions-newer-show">
              {t('decisions.list.newer.show')}
            </Button>
          </div>
        ) : null}

        <DataTable
          label={t('decisions.list.table.label')}
          columns={columns}
          rows={visiveis}
          rowKey={(d) => d.request_id}
          loading={decisoes.isLoading}
          error={sourceOutside ? null : (erro ?? null)}
          onRetry={() => void decisoes.refetch()}
          // Busca **local** (FR-096, T372): a indicação vive no campo e na nota ao
          // lado — quem busca precisa saber que o filtro não foi à fonte, senão lê
          // "nada encontrado" como "não existe".
          filters={
            <LocalSearchField
              value={search}
              onChange={setBusca}
              placeholder={t('decisions.list.searchPlaceholder')}
              helperText={t('decisions.list.searchHelper')}
            />
          }
          onRowClick={(d) => drawer.abrir(d.request_id)}
          emptyTitle={t('decisions.list.empty.title')}
          emptyMessage={t(
            search.trim() ? 'decisions.list.empty.search' : 'decisions.list.empty.message',
          )}
          emptyIcon="list"
        />

        {decisoes.hasMore ? (
          <div className={s.rodape}>
            <LoadingButton
              variant="out"
              size="sm"
              loading={decisoes.isFetchingNextPage}
              loadingLabel={t('decisions.list.loadMore.loading')}
              onClick={decisoes.carregarMais}
              data-testid="decisions-load-older"
            >
              {t('decisions.list.loadMore')}
            </LoadingButton>
          </div>
        ) : carregadas.length > 0 && !decisoes.isLoading ? (
          // O fim do período: o histórico não tem nada mais antigo nesta janela.
          <p className={[s.rodape, s.muted].join(' ')} data-testid="decisions-older-end">
            {t('decisions.list.olderEnd')}
          </p>
        ) : null}
      </Card>

      {/* Montado só com o drawer aberto, para o `DrawerPresence` guardar a decisão que sai (aim-xz9). */}
      <DrawerPresence>
        {drawer.id ? (
          <DecisionDrawer
            requestId={drawer.id}
            decision={carregadas.find((d) => d.request_id === drawer.id)}
            onClose={drawer.fechar}
          />
        ) : null}
      </DrawerPresence>
    </>
  );
}
